import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertOctagon, ChevronDown, ChevronRight } from "lucide-react";

const SHOW = 5;
const hrefOf = (pid, it) => `/processo/${pid}${it.target || (it.kind === "doc" ? `#doc-${it.key}` : "")}`;
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Sub-lista dos documentos em falta dentro do cartão «O que falta para a escritura».
export function DeedDocs({ p, items, others = false }) {
  const [all, setAll] = useState(false);
  const nDocs = items.filter((i) => i.kind === "doc").length;
  const shown = all ? items : items.slice(0, SHOW);
  return (
    <div className="border-t border-slate-100 px-[18px] md:px-6 py-3" data-testid="deed-blockers">
      <div className="text-xs font-bold tracking-wider text-slate-400 uppercase mb-1" data-testid="deed-blockers-sub">{nDocs === items.length ? (others ? "Outros documentos em falta" : "Documentos em falta") : "Outros pendentes"}</div>
      <ul>
        {shown.map((it) => (
          <li key={it.id}>
            <Link to={hrefOf(p.id, it)} className="flex items-center gap-3 py-2 text-[15px] hover:bg-slate-50 -mx-2 px-2 rounded-lg transition-colors" data-testid={`deed-blocker-${it.id}`}>
              <span className={`w-2 h-2 rounded-full shrink-0 ${it.kind === "doc" ? "bg-red-500" : "bg-amber-500"}`} />
              <span className="flex-1 min-w-0 font-medium text-slate-800">{it.label}{it.kind !== "doc" && it.why ? <span className="font-normal text-slate-500"> · {it.why}</span> : null}</span>
              <span className="text-xs font-semibold text-slate-500 shrink-0 inline-flex items-center gap-0.5">{it.kind === "doc" ? "Documento" : "Passo"} <ChevronRight size={14} /></span>
            </Link>
          </li>
        ))}
      </ul>
      {items.length > SHOW && (
        <button type="button" onClick={() => setAll(!all)} className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-ec-green" data-testid="deed-blockers-more">
          {all ? "Ver menos" : `Ver todos (${items.length})`} <ChevronDown size={14} className={`transition-transform ${all ? "rotate-180" : ""}`} />
        </button>
      )}
    </div>
  );
}

// O que impede a escritura (todo.deed do servidor), à vista em vez de escondido no chip do topo.
export function DeedBlockers({ p }) {
  const [all, setAll] = useState(false);
  const covered = new Set((p.a_tratar?.lines || []).flatMap((ln) => [ln.button?.doc_key, ...(ln.button?.doc_keys || []), ...(ln.button?.reminder_keys || []), ...(ln.remind?.reminder_keys || [])]).filter(Boolean));
  const items = (p.todo?.deed || []).filter((i) => !covered.has(i.key));
  if (p.stage === "angariacao" || ["escriturado", "sem_venda"].includes(p.status) || !items.length) return null;
  const sorted = [...items].sort((a, b) => (a.tone === "red" ? 0 : 1) - (b.tone === "red" ? 0 : 1));
  const blocks = (p.card?.blocks || []).length;
  const urgent = blocks > 0 || p.deed_overdue || (p.days_to_deed != null && p.days_to_deed <= 7);
  const shown = all ? sorted : sorted.slice(0, SHOW);
  return (
    <section className={`bg-white rounded-[18px] border-2 overflow-hidden ${urgent ? "border-red-200" : "border-amber-200"}`} data-testid="deed-blockers">
      <div className={`px-[18px] md:px-6 py-4 flex items-start gap-3 ${urgent ? "bg-red-50/70" : "bg-amber-50/60"}`}>
        <AlertOctagon size={22} className={`shrink-0 mt-0.5 ${urgent ? "text-red-600" : "text-amber-600"}`} />
        <div className="min-w-0">
          <h2 className="font-heading text-lg md:text-xl navy-text leading-tight">O que falta para a escritura <span className="text-slate-500 font-normal text-base" data-testid="deed-blockers-counts">({plural(items.length, "pendente", "pendentes")})</span></h2>
        </div>
      </div>
      <ul className="divide-y divide-slate-100">
        {shown.map((it) => (
          <li key={it.id}>
            <Link to={hrefOf(p.id, it)} className="flex items-center gap-3 px-[18px] md:px-6 py-3 hover:bg-slate-50 transition-colors" data-testid={`deed-blocker-${it.id}`}>
              <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${it.tone === "red" ? "bg-red-600" : "bg-amber-500"}`} />
              <span className="flex-1 min-w-0 text-[15px]">
                <span className="font-semibold text-slate-800">{it.label}</span>
                {it.why && it.why !== "Em falta" && <span className="text-slate-500"> · {it.why}</span>}
              </span>
              <span className="hidden sm:inline text-xs font-semibold text-slate-400 shrink-0">{it.kind === "doc" ? "Documento" : "Passo"}</span>
              <ChevronRight size={16} className="text-slate-400 shrink-0" />
            </Link>
          </li>
        ))}
      </ul>
      {items.length > SHOW && (
        <button type="button" onClick={() => setAll(!all)} className="w-full flex items-center gap-1.5 border-t border-slate-100 px-[18px] md:px-6 py-2.5 text-sm font-semibold text-ec-green hover:bg-slate-50" data-testid="deed-blockers-more">
          {all ? "Ver menos" : `Ver todos (${items.length})`} <ChevronDown size={14} className={`transition-transform ${all ? "rotate-180" : ""}`} />
        </button>
      )}
    </section>
  );
}
